const HISTORY_PERFORMANCE_SCHEMA_VERSION = 1
const HISTORY_PERFORMANCE_CAPACITY = 256
const MAX_DURATION_MS = 5 * 60 * 1000
const MAX_PAYLOAD_BYTES = 100 * 1024 * 1024

const OPERATION_RANGES = Object.freeze({
     chaac_log: new Set(["1h", "24h", "7d", "30d"]),
     sensor_history: new Set(["24h", "7d", "30d"]),
})
const LEGS = new Set(["research", "aggregate", "legacy"])
const OUTCOMES = new Set([
     "success",
     "http_error",
     "network_error",
     "aborted",
     "timeout",
     "invalid_json",
     "stale",
     "normalization_error",
])
const STATUS_FAMILIES = new Set(["2xx", "4xx", "5xx", "other", "none"])
const PHASE_PRECISIONS = new Set(["split", "combined", "none"])
const REQUEST_ID_PATTERN = /^[a-f0-9]{32}$/
const DURATION_FIELDS = ["ttfb_ms", "download_ms", "json_parse_ms", "decode_ms", "apply_ms", "total_ms"]

const samples = []

const monotonicNow = () => {
     if (typeof performance !== "undefined" && typeof performance.now === "function") return performance.now()
     return Date.now()
}

const finiteClampedInteger = (value, maximum) => {
     if (!Number.isFinite(value) || value < 0) return null
     return Math.min(maximum, Math.round(value))
}

const delta = (end, start) => finiteClampedInteger(Math.max(0, end - start), MAX_DURATION_MS)

const validDescriptor = (descriptor = {}) => {
     const { operation, range, leg } = descriptor || {}
     return Boolean(OPERATION_RANGES[operation]?.has(range) && LEGS.has(leg))
}

const sanitizeSample = (sample = {}) => {
     if (!validDescriptor(sample) || !OUTCOMES.has(sample.outcome)) return null
     const sanitized = {
          schema_version: HISTORY_PERFORMANCE_SCHEMA_VERSION,
          operation: sample.operation,
          range: sample.range,
          leg: sample.leg,
          outcome: sample.outcome,
          status_family: STATUS_FAMILIES.has(sample.status_family) ? sample.status_family : "none",
          phase_precision: PHASE_PRECISIONS.has(sample.phase_precision) ? sample.phase_precision : "none",
          request_id: REQUEST_ID_PATTERN.test(String(sample.request_id || "")) ? String(sample.request_id) : null,
          payload_bytes: finiteClampedInteger(sample.payload_bytes, MAX_PAYLOAD_BYTES),
     }
     DURATION_FIELDS.forEach((field) => { sanitized[field] = finiteClampedInteger(sample[field], MAX_DURATION_MS) })
     return Object.freeze(sanitized)
}

const percentile = (values, percentage) => {
     if (!values.length) return null
     const sorted = [...values].sort((left, right) => left - right)
     return sorted[Math.max(0, Math.ceil(percentage / 100 * sorted.length) - 1)]
}

const aggregateDurations = (entries) => Object.fromEntries(DURATION_FIELDS.map((field) => {
     const values = entries.map((entry) => entry[field]).filter(Number.isFinite)
     return [field, Object.freeze({
          count: values.length,
          p50: percentile(values, 50),
          p95: percentile(values, 95),
          max: values.length ? Math.max(...values) : null,
     })]
}))

export const recordHistoryPerformance = (sample) => {
     const sanitized = sanitizeSample(sample)
     if (!sanitized) return null
     samples.push(sanitized)
     if (samples.length > HISTORY_PERFORMANCE_CAPACITY) samples.splice(0, samples.length - HISTORY_PERFORMANCE_CAPACITY)
     if (typeof window !== "undefined" && typeof window.dispatchEvent === "function" && typeof CustomEvent === "function") {
          window.dispatchEvent(new CustomEvent("feeder-history-performance", { detail: sanitized }))
     }
     return sanitized
}

export const clearHistoryPerformance = () => { samples.splice(0, samples.length) }

export const historyPerformanceSnapshot = () => {
     const entries = samples.map((sample) => ({ ...sample }))
     const groups = new Map()
     entries.filter((entry) => entry.outcome === "success").forEach((entry) => {
          const key = `${entry.operation}:${entry.range}:${entry.leg}`
          if (!groups.has(key)) groups.set(key, [])
          groups.get(key).push(entry)
     })
     const aggregates = [...groups.entries()].map(([key, groupEntries]) => {
          const [operation, range, leg] = key.split(":")
          return Object.freeze({ operation, range, leg, sample_count: groupEntries.length, phases: aggregateDurations(groupEntries) })
     })
     return Object.freeze({
          schema_version: HISTORY_PERFORMANCE_SCHEMA_VERSION,
          capacity: HISTORY_PERFORMANCE_CAPACITY,
          sample_count: entries.length,
          samples: Object.freeze(entries),
          aggregates: Object.freeze(aggregates),
     })
}

export const historyUtf8ByteLength = (text) => {
     const value = String(text)
     if (typeof TextEncoder === "function") return new TextEncoder().encode(value).length
     return new Blob([value]).size
}

export const beginHistoryRequestTiming = (descriptor, clock = monotonicNow) => {
     if (!validDescriptor(descriptor)) return null
     const startedAt = clock()
     let headersAt = null
     let downloadedAt = null
     let payloadBytes = null
     let requestId = null
     let completed = null

     const base = (outcome, statusFamily, phasePrecision) => ({
          operation: descriptor.operation,
          range: descriptor.range,
          leg: descriptor.leg,
          outcome,
          status_family: statusFamily,
          phase_precision: phasePrecision,
          request_id: requestId,
          payload_bytes: payloadBytes,
     })

     return Object.freeze({
          markHeaders(value = null) {
               if (headersAt === null) headersAt = clock()
               if (REQUEST_ID_PATTERN.test(String(value || ""))) requestId = String(value)
          },
          markDownloaded(bytes = null) {
               if (headersAt === null) headersAt = clock()
               if (downloadedAt === null) downloadedAt = clock()
               payloadBytes = finiteClampedInteger(bytes, MAX_PAYLOAD_BYTES)
          },
          finishSplit(outcome = "success", statusFamily = "2xx") {
               if (completed) return completed
               const parsedAt = clock()
               if (headersAt === null) headersAt = parsedAt
               if (downloadedAt === null) downloadedAt = parsedAt
               completed = Object.freeze({
                    ...base(OUTCOMES.has(outcome) ? outcome : "network_error", statusFamily, "split"),
                    ttfb_ms: delta(headersAt, startedAt),
                    download_ms: delta(downloadedAt, headersAt),
                    json_parse_ms: delta(parsedAt, downloadedAt),
                    decode_ms: null,
                    apply_ms: null,
                    total_ms: delta(parsedAt, startedAt),
               })
               return completed
          },
          finishCombined(outcome = "success", statusFamily = "2xx") {
               if (completed) return completed
               const decodedAt = clock()
               if (headersAt === null) headersAt = decodedAt
               completed = Object.freeze({
                    ...base(OUTCOMES.has(outcome) ? outcome : "network_error", statusFamily, "combined"),
                    ttfb_ms: delta(headersAt, startedAt),
                    download_ms: null,
                    json_parse_ms: null,
                    decode_ms: delta(decodedAt, headersAt),
                    apply_ms: null,
                    total_ms: delta(decodedAt, startedAt),
               })
               return completed
          },
          fail(outcome = "network_error", statusFamily = "none") {
               if (completed) return completed
               const failedAt = clock()
               const precision = downloadedAt === null ? "none" : "split"
               completed = Object.freeze({
                    ...base(OUTCOMES.has(outcome) ? outcome : "network_error", statusFamily, precision),
                    ttfb_ms: headersAt === null ? null : delta(headersAt, startedAt),
                    download_ms: headersAt === null || downloadedAt === null ? null : delta(downloadedAt, headersAt),
                    json_parse_ms: downloadedAt === null ? null : delta(failedAt, downloadedAt),
                    decode_ms: null,
                    apply_ms: null,
                    total_ms: delta(failedAt, startedAt),
               })
               return completed
          },
     })
}

export const beginHistoryApplyTiming = (networkSample, clock = monotonicNow, recorder = recordHistoryPerformance) => {
     const startedAt = clock()
     let completed = null
     return (outcome = networkSample?.outcome || "success") => {
          if (completed) return completed
          const applyMs = delta(clock(), startedAt)
          const networkTotal = finiteClampedInteger(networkSample?.total_ms, MAX_DURATION_MS) || 0
          completed = recorder({
               ...networkSample,
               outcome: OUTCOMES.has(outcome) ? outcome : "normalization_error",
               apply_ms: applyMs,
               total_ms: finiteClampedInteger(networkTotal + applyMs, MAX_DURATION_MS),
          })
          return completed
     }
}
